import type { Node } from '../../../core/models/scene';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useViewRulesStore } from '../../../core/assistant/stores/viewRulesStore';
import { getLaneLabelForNode } from '../services/laneContainment';

/**
 * Process-domain computed lenses.
 *
 * Mirrors the Network domain's `computedLenses.ts` pattern but smaller —
 * each lens stamps a synthetic `__lens_*` attribute on every applicable
 * node, then registers a colorBy / heatmap view rule on that attribute.
 * Teardown wipes the stamps and removes the rule.
 *
 * Why not just use a regular field-pack lens (like `owner`)? Because
 * lane membership is *implicit* on a fresh diagram — a step dropped
 * into a swim lane only gets `attrs.__laneLabel` if it went through
 * RDM hydration. The compute pass derives lane membership lazily from
 * pool-table geometry (see `laneContainment.ts`), which keeps the lens
 * working on hand-drawn diagrams that never touched RDM.
 */

const LENS_ATTR_PREFIX = '__lens_';

export type ProcessLensId = 'lane' | 'tag';

interface LensSpec {
  id: ProcessLensId;
  attrKey: string;
  displayName: string;
  ruleKind: 'colorBy' | 'heatmap';
  compute: (node: Node, allNodes: readonly Node[]) => string | number | undefined;
}

const LENSES: Record<ProcessLensId, LensSpec> = {
  lane: {
    id: 'lane',
    attrKey: `${LENS_ATTR_PREFIX}lane`,
    displayName: 'Lane',
    ruleKind: 'colorBy',
    compute: (node, allNodes) => getLaneLabelForNode(node, allNodes),
  },
  // Tag colour-by — picks the *first* tag value when a node has many.
  // Multi-tag nodes will show under the colour of whichever tag is
  // first in the array; for the v1 chip experience this is fine
  // (users can reorder by removing-and-readding).
  tag: {
    id: 'tag',
    attrKey: `${LENS_ATTR_PREFIX}tag`,
    displayName: 'Tag',
    ruleKind: 'colorBy',
    compute: (node) => {
      const tags = ((node.attrs ?? {}) as Record<string, unknown>).tags;
      if (!Array.isArray(tags) || tags.length === 0) return undefined;
      const first = tags[0];
      return typeof first === 'string' && first.length > 0 ? first : undefined;
    },
  },
};

const ALL_LENS_ATTR_KEYS = new Set<string>(Object.values(LENSES).map((l) => l.attrKey));

export interface ApplyLensResult {
  ruleId: string;
  affected: number;
}

export function applyLens(lensId: ProcessLensId): ApplyLensResult | null {
  const spec = LENSES[lensId];
  if (!spec) return null;

  const diagramStore = useDiagramStore.getState();
  const viewStore = useViewRulesStore.getState();
  const nodes = diagramStore.nodes;

  const updates: Array<{ id: string; updates: Partial<Node> }> = [];
  let affected = 0;

  for (const node of nodes) {
    const value = spec.compute(node, nodes);
    const currentAttrs = (node.attrs as Record<string, unknown> | undefined) ?? {};

    // Strip stamps from every other computed lens up-front so a new
    // lens application lands on a clean synthetic-key slate. Field-pack
    // attributes (real data) are untouched.
    const nextAttrs: Record<string, unknown> = { ...currentAttrs };
    let mutated = false;
    for (const key of ALL_LENS_ATTR_KEYS) {
      if (key === spec.attrKey) continue;
      if (key in nextAttrs) {
        delete nextAttrs[key];
        mutated = true;
      }
    }

    if (value === undefined) {
      if (spec.attrKey in nextAttrs) {
        delete nextAttrs[spec.attrKey];
        mutated = true;
      }
      if (mutated) updates.push({ id: node.id, updates: { attrs: nextAttrs } });
      continue;
    }

    if (currentAttrs[spec.attrKey] === value && !mutated) continue;
    nextAttrs[spec.attrKey] = value;
    updates.push({ id: node.id, updates: { attrs: nextAttrs } });
    affected++;
  }

  if (updates.length > 0) {
    diagramStore.updateNodes(updates, true);
  }

  // Drop any existing rule for this lens, then add a fresh one. Other
  // computed-lens rules in this domain stay untouched (we only have
  // one lens today; this scaffold lets us add more without rework).
  for (const otherSpec of Object.values(LENSES)) {
    const otherRuleId = findRuleIdByAttrKey(otherSpec.attrKey);
    if (otherRuleId) viewStore.removeRule(otherRuleId);
  }

  const ruleId = viewStore.addViewRule(spec.ruleKind, {
    field: spec.attrKey,
    name: spec.displayName,
  });

  return { ruleId, affected };
}

export function clearLens(lensId: ProcessLensId): boolean {
  const spec = LENSES[lensId];
  if (!spec) return false;

  const diagramStore = useDiagramStore.getState();
  const viewStore = useViewRulesStore.getState();

  const ruleId = findRuleIdByAttrKey(spec.attrKey);
  if (ruleId) viewStore.removeRule(ruleId);

  const updates: Array<{ id: string; updates: Partial<Node> }> = [];
  for (const node of diagramStore.nodes) {
    const attrs = (node.attrs as Record<string, unknown> | undefined) ?? {};
    if (!(spec.attrKey in attrs)) continue;
    const nextAttrs: Record<string, unknown> = { ...attrs };
    delete nextAttrs[spec.attrKey];
    updates.push({ id: node.id, updates: { attrs: nextAttrs } });
  }
  if (updates.length > 0) {
    diagramStore.updateNodes(updates, true);
  }
  return true;
}

export function isLensActive(lensId: ProcessLensId): boolean {
  const spec = LENSES[lensId];
  if (!spec) return false;
  return findRuleIdByAttrKey(spec.attrKey) !== null;
}

/**
 * Pure variant of `isLensActive` that takes the rules array as input so
 * React components can drive their reactivity off a subscribed Zustand
 * slice (and satisfy `react-hooks/exhaustive-deps`).
 */
export function isLensActiveIn(
  lensId: ProcessLensId,
  rules: ReadonlyArray<{
    enabled?: boolean;
    display?: {
      colorByField?: { field?: string };
      heatmap?: { field?: string };
    };
  }>,
): boolean {
  const spec = LENSES[lensId];
  if (!spec) return false;
  return rules.some(
    (r) =>
      r.enabled === true &&
      (r.display?.colorByField?.field === spec.attrKey ||
        r.display?.heatmap?.field === spec.attrKey),
  );
}

function findRuleIdByAttrKey(attrKey: string): string | null {
  const rules = useViewRulesStore.getState().rules;
  const match = rules.find((r) => {
    if (!r.enabled) return false;
    return r.display?.colorByField?.field === attrKey || r.display?.heatmap?.field === attrKey;
  });
  return match?.id ?? null;
}
